import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactFlow, {
    ReactFlowProvider,
    addEdge,
    useNodesState,
    useEdgesState,
    Controls,
    MiniMap,
    Background,
    useReactFlow,
    Panel,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
    PlusIcon,
    XMarkIcon,
    PlayIcon,
    BookmarkIcon,
    Cog6ToothIcon,
    EyeSlashIcon,
    EyeIcon,
    ArrowsPointingOutIcon,
    ArrowsPointingInIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    InformationCircleIcon,
    TrashIcon,
    PhotoIcon,
    DocumentIcon,
    FilmIcon,
    MusicalNoteIcon
} from '@heroicons/react/24/outline';

// Import custom node components
import {
    StartNode,
    MessageNode,
    QuestionNode,
    ConditionNode,
    ActionNode,
    HandoffNode,
    EndNode
} from './nodes/CustomNodeComponents';

// Node Types
const nodeTypes = {
    start: StartNode,
    message: MessageNode,
    question: QuestionNode,
    condition: ConditionNode,
    action: ActionNode,
    handoff: HandoffNode,
    end: EndNode,
};


// Enhanced Key-Value Pair Component
const KeyValuePairInput = ({
    pairs = [],
    onChange,
    keyPlaceholder = "Key",
    valuePlaceholder = "Value",
    helpText,
    allowVariables = true,
    className = ""
}) => {
    const handlePairChange = (index, field, value) => {
        const newPairs = [...pairs];
        newPairs[index] = { ...newPairs[index], [field]: value };
        onChange(newPairs);
    };

    const addPair = () => {
        onChange([...pairs, { key: '', value: '' }]);
    };

    const removePair = (index) => {
        const newPairs = pairs.filter((_, i) => i !== index);
        onChange(newPairs);
    };

    return (
        <div className={className}>
            {helpText && (
                <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg mb-3">
                    <InformationCircleIcon className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-blue-700">{helpText}</p>
                </div>
            )}

            <div className="space-y-2">
                {pairs.map((pair, index) => (
                    <div key={index} className="flex flex-col gap-2">
                        <input
                            value={pair.key || ''}
                            onChange={(e) => handlePairChange(index, 'key', e.target.value)}
                            className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                            placeholder={keyPlaceholder}
                        />
                        <input
                            value={pair.value || ''}
                            onChange={(e) => handlePairChange(index, 'value', e.target.value)}
                            className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                            placeholder={valuePlaceholder}
                        />
                        <button
                            onClick={() => removePair(index)}
                            className="px-2 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors w-fit self-end"
                            title="Remove"
                        >
                            <TrashIcon className="h-4 w-4" />
                        </button>
                    </div>
                ))}
            </div>

            <button
                onClick={addPair}
                className="mt-2 w-full px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 text-sm border border-dashed border-gray-300"
            >
                <PlusIcon className="h-4 w-4" />
                Add {keyPlaceholder}
            </button>
        </div>
    );
};

// Collapsible Section Component
const CollapsibleSection = ({ title, children, defaultOpen = false, icon }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <div className="border border-gray-200 rounded-lg mb-4">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-t-lg transition-colors"
            >
                <div className="flex items-center gap-2">
                    {icon}
                    <span className="font-medium text-gray-900">{title}</span>
                </div>
                {isOpen ? (
                    <ChevronUpIcon className="h-4 w-4 text-gray-500" />
                ) : (
                    <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                )}
            </button>
            {isOpen && (
                <div className="p-4 border-t border-gray-200">
                    {children}
                </div>
            )}
        </div>
    );
};

// Enhanced Action Node Configuration
const ActionNodeConfig = ({ nodeData, setNodeData }) => {
    // Helper functions to convert between key-value pairs and objects
    const objectToKeyValuePairs = (obj) => {
        if (!obj || typeof obj !== 'object') return [];
        return Object.entries(obj).map(([key, value]) => ({ key, value: String(value) }));
    };

    const keyValuePairsToObject = (pairs) => {
        const obj = {};
        pairs.forEach(({ key, value }) => {
            if (key.trim()) {
                obj[key.trim()] = value;
            }
        });
        return obj;
    };

    // State for key-value pairs
    const [headerPairs, setHeaderPairs] = useState(() =>
        objectToKeyValuePairs(nodeData.action?.config?.headers)
    );
    const [queryPairs, setQueryPairs] = useState(() =>
        objectToKeyValuePairs(nodeData.action?.config?.queryParams)
    );
    const [bodyPairs, setBodyPairs] = useState(() =>
        objectToKeyValuePairs(nodeData.action?.config?.body)
    );
    const [responseMappingPairs, setResponseMappingPairs] = useState(() =>
        objectToKeyValuePairs(nodeData.action?.config?.responseMapping?.data)
    );
    const [errorMappingPairs, setErrorMappingPairs] = useState(() =>
        objectToKeyValuePairs(nodeData.action?.config?.errorMapping)
    );

    // Update nodeData when pairs change
    useEffect(() => {
        setNodeData(prev => ({
            ...prev,
            action: {
                ...prev.action,
                config: {
                    ...prev.action?.config,
                    headers: keyValuePairsToObject(headerPairs),
                    queryParams: keyValuePairsToObject(queryPairs),
                    body: keyValuePairsToObject(bodyPairs),
                    responseMapping: {
                        ...prev.action?.config?.responseMapping,
                        data: keyValuePairsToObject(responseMappingPairs)
                    },
                    errorMapping: keyValuePairsToObject(errorMappingPairs)
                }
            }
        }));
    }, [headerPairs, queryPairs, bodyPairs, responseMappingPairs, errorMappingPairs, setNodeData]);

    const updateConfig = (field, value) => {
        setNodeData(prev => ({
            ...prev,
            action: {
                ...prev.action,
                config: {
                    ...prev.action?.config,
                    [field]: value
                }
            }
        }));
    };

    const isPostMethod = ['POST', 'PUT', 'PATCH'].includes(nodeData.action?.config?.method);

    return (
        <>
            {/* Basic Configuration */}
            <div className="space-y-4 mb-6">
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">API Name</label>
                    <input
                        value={nodeData.action?.config?.name || ''}
                        onChange={(e) => updateConfig('name', e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder="e.g., getWeather, createUser"
                    />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Method</label>
                        <select
                            value={nodeData.action?.config?.method || 'GET'}
                            onChange={(e) => updateConfig('method', e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        >
                            <option value="GET">GET</option>
                            <option value="POST">POST</option>
                            <option value="PUT">PUT</option>
                            <option value="DELETE">DELETE</option>
                            <option value="PATCH">PATCH</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Timeout (ms)</label>
                        <input
                            type="number"
                            value={nodeData.action?.config?.timeout || 10000}
                            onChange={(e) => updateConfig('timeout', parseInt(e.target.value) || 10000)}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                            placeholder="10000"
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">URL</label>
                    <input
                        value={nodeData.action?.config?.url || ''}
                        onChange={(e) => updateConfig('url', e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder="https://api.example.com/endpoint"
                    />
                </div>
            </div>

            {/* Advanced Configuration - Collapsible Sections */}
            <CollapsibleSection
                title="Headers"
                icon={<Cog6ToothIcon className="h-4 w-4 text-gray-600" />}
            >
                <KeyValuePairInput
                    pairs={headerPairs}
                    onChange={setHeaderPairs}
                    keyPlaceholder="Header Name"
                    valuePlaceholder="Header Value"
                    helpText="Add HTTP headers for your request. Common examples: Content-Type, Authorization, User-Agent"
                />
            </CollapsibleSection>

            <CollapsibleSection
                title="Query Parameters"
                icon={<Cog6ToothIcon className="h-4 w-4 text-gray-600" />}
            >
                <KeyValuePairInput
                    pairs={queryPairs}
                    onChange={setQueryPairs}
                    keyPlaceholder="Parameter Name"
                    valuePlaceholder="Parameter Value"
                    helpText="Add URL query parameters. Use {{variable_name}} to include bot variables in values."
                />
            </CollapsibleSection>

            {/* Request Body (only for POST, PUT, PATCH) */}
            {isPostMethod && (
                <CollapsibleSection
                    title="Request Body"
                    defaultOpen={true}
                    icon={<Cog6ToothIcon className="h-4 w-4 text-gray-600" />}
                >
                    <div className="mb-4">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Body Type</label>
                        <select
                            value={nodeData.action?.config?.bodyType || 'json'}
                            onChange={(e) => updateConfig('bodyType', e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        >
                            <option value="json">JSON</option>
                            <option value="form">Form Data</option>
                        </select>
                    </div>

                    <KeyValuePairInput
                        pairs={bodyPairs}
                        onChange={setBodyPairs}
                        keyPlaceholder="Field Name"
                        valuePlaceholder="Field Value"
                        helpText="Add request body fields. Use {{variable_name}} to include bot variables in values."
                    />
                </CollapsibleSection>
            )}

            {/* Response Mapping */}
            <CollapsibleSection
                title="Response Mapping"
                defaultOpen={true}
                icon={<Cog6ToothIcon className="h-4 w-4 text-gray-600" />}
            >
                <KeyValuePairInput
                    pairs={responseMappingPairs}
                    onChange={setResponseMappingPairs}
                    keyPlaceholder="Variable Name"
                    valuePlaceholder="Response Path"
                    helpText="Map API response fields to bot variables. Use dot notation for nested fields (e.g., 'data.temperature' or 'user.profile.name')"
                />
            </CollapsibleSection>

            {/* Success/Error Variables */}
            <CollapsibleSection
                title="Success & Error Handling"
                icon={<Cog6ToothIcon className="h-4 w-4 text-gray-600" />}
            >
                <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-4">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Success Variable</label>
                            <input
                                value={nodeData.action?.config?.successVariable || ''}
                                onChange={(e) => updateConfig('successVariable', e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                placeholder="e.g., api_success"
                            />
                            <p className="text-xs text-gray-500 mt-1">Variable set to 'true' on success</p>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Error Variable</label>
                            <input
                                value={nodeData.action?.config?.errorVariable || ''}
                                onChange={(e) => updateConfig('errorVariable', e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                placeholder="e.g., api_error"
                            />
                            <p className="text-xs text-gray-500 mt-1">Variable set to error message on failure</p>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Error Response Mapping</label>
                        <KeyValuePairInput
                            pairs={errorMappingPairs}
                            onChange={setErrorMappingPairs}
                            keyPlaceholder="Variable Name"
                            valuePlaceholder="Error Response Path"
                            helpText="Map error response fields to variables (optional). Use for detailed error handling."
                        />
                    </div>
                </div>
            </CollapsibleSection>

            {/* API Testing Preview */}
            <CollapsibleSection
                title="Request Preview"
                icon={<InformationCircleIcon className="h-4 w-4 text-gray-600" />}
            >
                <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm font-mono">
                        <div className="text-blue-600 font-semibold">
                            {nodeData.action?.config?.method || 'GET'} {nodeData.action?.config?.url || 'https://api.example.com/endpoint'}
                        </div>

                        {headerPairs.length > 0 && (
                            <div className="mt-2">
                                <div className="text-gray-600 font-semibold">Headers:</div>
                                {headerPairs.map((pair, i) => (
                                    <div key={i} className="text-gray-800 ml-2">
                                        {pair.key}: {pair.value}
                                    </div>
                                ))}
                            </div>
                        )}

                        {queryPairs.length > 0 && (
                            <div className="mt-2">
                                <div className="text-gray-600 font-semibold">Query Parameters:</div>
                                {queryPairs.map((pair, i) => (
                                    <div key={i} className="text-gray-800 ml-2">
                                        {pair.key}={pair.value}
                                    </div>
                                ))}
                            </div>
                        )}

                        {isPostMethod && bodyPairs.length > 0 && (
                            <div className="mt-2">
                                <div className="text-gray-600 font-semibold">Body:</div>
                                <div className="text-gray-800 ml-2">
                                    {JSON.stringify(keyValuePairsToObject(bodyPairs), null, 2)}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </CollapsibleSection>
        </>
    );
};

// Media Configuration Panel for Message Nodes
const MediaConfigPanel = ({ mediaConfig, onChange }) => {
    const [media, setMedia] = useState(mediaConfig || { enabled: false, type: 'image', url: '', caption: '' });

    useEffect(() => {
        onChange(media);
    }, [media, onChange]);

    const handleMediaChange = (field, value) => {
        setMedia(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const getMediaIcon = (type) => {
        switch (type) {
            case 'image': return <PhotoIcon className="h-4 w-4" />;
            case 'document': return <DocumentIcon className="h-4 w-4" />;
            case 'video': return <FilmIcon className="h-4 w-4" />;
            case 'audio': return <MusicalNoteIcon className="h-4 w-4" />;
            default: return <DocumentIcon className="h-4 w-4" />;
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                    <span>Include Media</span>
                </label>
                <div className="relative inline-block w-10 align-middle select-none">
                    <input
                        type="checkbox"
                        name="media-toggle"
                        id="media-toggle"
                        checked={media.enabled}
                        onChange={(e) => handleMediaChange('enabled', e.target.checked)}
                        className="sr-only"
                    />
                    <label
                        htmlFor="media-toggle"
                        className={`block overflow-hidden h-6 rounded-full cursor-pointer transition-colors duration-200 ease-in-out ${media.enabled ? 'bg-blue-500' : 'bg-gray-300'}`}
                    >
                        <span
                            className={`block h-6 w-6 rounded-full bg-white shadow transform transition-transform duration-200 ease-in-out ${media.enabled ? 'translate-x-4' : 'translate-x-0'}`}
                        />
                    </label>
                </div>
            </div>

            {media.enabled && (
                <>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Media Type</label>
                        <div className="grid grid-cols-2 gap-2">
                            {['image', 'document', 'video', 'audio'].map((type) => (
                                <button
                                    key={type}
                                    type="button"
                                    onClick={() => handleMediaChange('type', type)}
                                    className={`flex items-center gap-2 p-2 rounded-lg border transition-colors ${media.type === type ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                                >
                                    {getMediaIcon(type)}
                                    <span className="text-sm capitalize">{type}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Media URL</label>
                        <input
                            type="text"
                            value={media.url || ''}
                            onChange={(e) => handleMediaChange('url', e.target.value)}
                            placeholder="https://example.com/media.jpg"
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            You can use variables like {'{{'}'variable_name{'}}'}
                        </p>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Caption (optional)</label>
                        <input
                            type="text"
                            value={media.caption || ''}
                            onChange={(e) => handleMediaChange('caption', e.target.value)}
                            placeholder="Media caption"
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>

                    {media.type === 'document' && (
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Filename (optional)</label>
                            <input
                                type="text"
                                value={media.filename || ''}
                                onChange={(e) => handleMediaChange('filename', e.target.value)}
                                placeholder="document.pdf"
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

// Improved Node Palette - Now a collapsible toolbar
const NodePalette = ({ onAddNode, isCollapsed, onToggle }) => {
    const nodeTemplates = [
        { type: 'start', label: 'Start', icon: '▶️', color: '#10b981' },
        { type: 'message', label: 'Message', icon: '💬', color: '#3b82f6' },
        { type: 'question', label: 'Question', icon: '❓', color: '#8b5cf6' },
        { type: 'condition', label: 'Condition', icon: '🔀', color: '#f59e0b' },
        { type: 'action', label: 'Action', icon: '⚡', color: '#ef4444' },
        { type: 'handoff', label: 'Handoff', icon: '👤', color: '#06b6d4' },
        { type: 'end', label: 'End', icon: '🏁', color: '#6b7280' },
    ];

    return (
        <div className="absolute top-4 left-4 z-20 bg-white rounded-lg shadow-lg border border-gray-200">
            {/* Palette Header */}
            <div className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-blue-100 rounded-t-lg border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <PlusIcon className="h-4 w-4" />
                    Add Nodes
                </h3>
                <button
                    onClick={onToggle}
                    className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                >
                    {isCollapsed ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronUpIcon className="h-4 w-4" />}
                </button>
            </div>

            {/* Palette Content */}
            {!isCollapsed && (
                <div className="p-3">
                    <div className="grid grid-cols-2 gap-2 w-64">
                        {nodeTemplates.map((template) => (
                            <button
                                key={template.type}
                                onClick={() => onAddNode(template.type)}
                                className="flex items-center p-3 border rounded-lg hover:bg-gray-50 transition-all duration-200 hover:shadow-md group"
                                style={{ borderColor: template.color + '40' }}
                            >
                                <span className="text-lg mr-2 group-hover:scale-110 transition-transform">{template.icon}</span>
                                <div className="text-left">
                                    <span className="text-sm font-medium text-gray-700 block">{template.label}</span>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// Improved Property Panel - Now a slide-out drawer
const PropertyPanel = ({ selectedNode, onUpdateNode, onClose, isOpen }) => {
    const [nodeData, setNodeData] = useState(selectedNode?.data || {});

    useEffect(() => {
        setNodeData(selectedNode?.data || {});
    }, [selectedNode]);

    const handleSave = () => {
        onUpdateNode(selectedNode.id, nodeData);
        onClose();
    };

    if (!selectedNode || !isOpen) return null;

    const renderFields = () => {
        switch (selectedNode.type) {
            case 'message':
                return (
                    <>
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Message Text</label>
                            <textarea
                                value={nodeData.message || ''}
                                onChange={(e) => setNodeData({ ...nodeData, message: e.target.value })}
                                className="w-full p-3 border border-gray-300 rounded-lg h-24 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                placeholder="Enter your message..."
                            />
                        </div>

                        <CollapsibleSection
                            title="Media Attachment"
                            icon={<PhotoIcon className="h-4 w-4 text-gray-600" />}
                        >
                            <MediaConfigPanel
                                mediaConfig={nodeData.media}
                                onChange={(mediaConfig) => setNodeData({ ...nodeData, media: mediaConfig })}
                            />
                        </CollapsibleSection>
                    </>
                );

            case 'question':
                return (
                    <>
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Question Text</label>
                            <textarea
                                value={nodeData.message || ''}
                                onChange={(e) => setNodeData({ ...nodeData, message: e.target.value })}
                                className="w-full p-3 border border-gray-300 rounded-lg h-24 focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                                placeholder="What would you like to ask?"
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Variable Name</label>
                            <input
                                value={nodeData.variable || ''}
                                onChange={(e) => setNodeData({ ...nodeData, variable: e.target.value })}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                placeholder="variable_name"
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Quick Replies</label>
                            <div className="space-y-2">
                                {(nodeData.quickReplies || []).map((reply, index) => (
                                    <div key={index} className="flex gap-2">
                                        <input
                                            value={reply || ''}
                                            onChange={(e) => {
                                                const newReplies = [...(nodeData.quickReplies || [])];
                                                newReplies[index] = e.target.value;
                                                setNodeData({ ...nodeData, quickReplies: newReplies });
                                            }}
                                            className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                            placeholder="Quick reply"
                                        />
                                        <button
                                            onClick={() => {
                                                const newReplies = (nodeData.quickReplies || []).filter((_, i) => i !== index);
                                                setNodeData({ ...nodeData, quickReplies: newReplies });
                                            }}
                                            className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                                        >
                                            <XMarkIcon className="h-4 w-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <button
                                onClick={() => {
                                    const newReplies = [...(nodeData.quickReplies || []), ''];
                                    setNodeData({ ...nodeData, quickReplies: newReplies });
                                }}
                                className="mt-2 w-full px-3 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors flex items-center justify-center gap-2"
                            >
                                <PlusIcon className="h-4 w-4" />
                                Add Quick Reply
                            </button>
                        </div>
                    </>
                );

            case 'condition':
                return (
                    <>
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Field to Check</label>
                            <input
                                value={nodeData.condition?.field || ''}
                                onChange={(e) => setNodeData({
                                    ...nodeData,
                                    condition: { ...nodeData.condition, field: e.target.value }
                                })}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                placeholder="variable_name"
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Operator</label>
                            <select
                                value={nodeData.condition?.operator || ''}
                                onChange={(e) => setNodeData({
                                    ...nodeData,
                                    condition: { ...nodeData.condition, operator: e.target.value }
                                })}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            >
                                <option value="">Select</option>
                                <option value="equals">Equals</option>
                                <option value="contains">Contains</option>
                                <option value="startsWith">Starts With</option>
                                <option value="endsWith">Ends With</option>
                                <option value="regex">Regex</option>
                            </select>
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Value</label>
                            <input
                                value={nodeData.condition?.value || ''}
                                onChange={(e) => setNodeData({
                                    ...nodeData,
                                    condition: { ...nodeData.condition, value: e.target.value }
                                })}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                placeholder="Expected value"
                            />
                        </div>
                    </>
                );

            case 'handoff':
                return (
                    <>
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Handoff Message</label>
                            <textarea
                                value={nodeData.message || ''}
                                onChange={(e) => setNodeData({ ...nodeData, message: e.target.value })}
                                className="w-full p-3 border border-gray-300 rounded-lg h-24 focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none"
                                placeholder="Message before transferring to human agent..."
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Handoff Reason</label>
                            <input
                                value={nodeData.handoffReason || ''}
                                onChange={(e) => setNodeData({ ...nodeData, handoffReason: e.target.value })}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                                placeholder="Internal reason for handoff"
                            />
                        </div>
                    </>
                );

            case 'action':
                return (
                    <>
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Action Type</label>
                            <select
                                value={nodeData.action?.type || ''}
                                onChange={(e) => setNodeData({
                                    ...nodeData,
                                    action: { ...nodeData.action, type: e.target.value }
                                })}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                            >
                                <option value="">Select</option>
                                <option value="setVariable">Set Variable</option>
                                <option value="apiCall">API Call</option>
                            </select>
                        </div>
                        {nodeData.action?.type === 'setVariable' && (
                            <>
                                <div className="mb-4">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Variable Name</label>
                                    <input
                                        value={nodeData.action?.config?.variable || ''}
                                        onChange={(e) => setNodeData({
                                            ...nodeData,
                                            action: {
                                                ...nodeData.action,
                                                config: { ...nodeData.action?.config, variable: e.target.value }
                                            }
                                        })}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                        placeholder="variable_name"
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Value</label>
                                    <input
                                        value={nodeData.action?.config?.value || ''}
                                        onChange={(e) => setNodeData({
                                            ...nodeData,
                                            action: {
                                                ...nodeData.action,
                                                config: { ...nodeData.action?.config, value: e.target.value }
                                            }
                                        })}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                        placeholder="value"
                                    />
                                </div>
                            </>
                        )}
                        {nodeData.action?.type === 'apiCall' && (
                            <ActionNodeConfig nodeData={nodeData} setNodeData={setNodeData} />
                        )}
                        {nodeData.action?.type === 'database' && (
                            <div className="p-4 bg-gray-100 rounded-lg text-sm text-gray-600">
                                Database operations configuration will be available in a future update.
                            </div>
                        )}
                    </>
                );

            default:
                return (
                    <div className="mb-4">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Label</label>
                        <input
                            value={nodeData.label || ''}
                            onChange={(e) => setNodeData({ ...nodeData, label: e.target.value })}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Node label"
                        />
                    </div>
                );
        }
    };

    return (
        <div className={`fixed top-0 right-0 h-full w-80 bg-white shadow-2xl transform transition-transform duration-300 z-30 ${isOpen ? 'translate-x-0' : 'translate-x-full'
            }`}>
            <div className="flex flex-col h-full">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <Cog6ToothIcon className="h-5 w-5" />
                        Node Properties
                    </h3>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4">
                    <div className="mb-4">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Node Label</label>
                        <input
                            value={nodeData.label || ''}
                            onChange={(e) => setNodeData({ ...nodeData, label: e.target.value })}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Node label"
                        />
                    </div>
                    {renderFields()}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-200 bg-gray-50">
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors font-medium"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                        >
                            Save Changes
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Main BotFlowCanvas Component
const BotFlowCanvas = ({ flowData, onSave, onTest, readOnly = false }) => {
    const [nodes, setNodes, onNodesChange] = useNodesState(flowData?.nodes || []);
    const [edges, setEdges, onEdgesChange] = useEdgesState(flowData?.edges || []);
    const [selectedNode, setSelectedNode] = useState(null);
    const [showPropertyPanel, setShowPropertyPanel] = useState(false);
    const [showNodePalette, setShowNodePalette] = useState(!readOnly);
    const [nodePaletteCollapsed, setNodePaletteCollapsed] = useState(false);
    const [reactFlowInstance, setReactFlowInstance] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(flowData?._uiState?.isFullscreen || false);
    const [showMiniMap, setShowMiniMap] = useState(true);
    const reactFlowWrapper = useRef(null);
    const { project } = useReactFlow();

    // Update fullscreen state when flowData changes and has a different fullscreen state
    useEffect(() => {
        if (flowData?._uiState?.isFullscreen !== undefined &&
            flowData._uiState.isFullscreen !== isFullscreen) {
            setIsFullscreen(flowData._uiState.isFullscreen);
        }
    }, [flowData?._uiState?.isFullscreen]);

    const onInit = (rfi) => setReactFlowInstance(rfi);

    // Custom node change handler to add visual indication of selection
    const handleNodesChange = (changes) => {
        onNodesChange(changes);

        // Handle selection changes to provide visual feedback
        const selectionChanges = changes.filter(change =>
            change.type === 'select' && change.selected !== undefined
        );

        if (selectionChanges.length > 0) {
            setNodes(nds => nds.map(node => {
                const change = selectionChanges.find(c => c.id === node.id);
                if (change) {
                    return {
                        ...node,
                        style: {
                            ...node.style,
                            boxShadow: change.selected ? '0 0 0 2px #f87171' : undefined,
                            borderRadius: '8px'
                        }
                    };
                }
                return node;
            }));
        }
    };

    // Custom edge change handler to add visual indication of selection
    const handleEdgesChange = (changes) => {
        onEdgesChange(changes);

        // Handle selection changes to provide visual feedback
        const selectionChanges = changes.filter(change =>
            change.type === 'select' && change.selected !== undefined
        );

        if (selectionChanges.length > 0) {
            setEdges(eds => eds.map(edge => {
                const change = selectionChanges.find(c => c.id === edge.id);
                if (change) {
                    // Store original color in a property when first selected
                    const originalStroke = edge.data?.originalStroke || edge.style?.stroke || '#6b7280';
                    const originalWidth = edge.data?.originalWidth || edge.style?.strokeWidth || 2;

                    if (change.selected) {
                        // When selected, store original values and apply highlighted style
                        return {
                            ...edge,
                            data: {
                                ...edge.data,
                                originalStroke: originalStroke,
                                originalWidth: originalWidth
                            },
                            style: {
                                ...edge.style,
                                stroke: '#f87171',
                                strokeWidth: 3
                            }
                        };
                    } else {
                        // When deselected, restore original values
                        return {
                            ...edge,
                            style: {
                                ...edge.style,
                                stroke: originalStroke,
                                strokeWidth: originalWidth
                            }
                        };
                    }
                }
                return edge;
            }));
        }
    };

    // Handle keyboard events for deleting nodes and edges
    useEffect(() => {
        if (readOnly) return;

        const handleKeyDown = (event) => {
            // Handle both Delete (46) and Backspace (8) keys for cross-platform support
            if (event.keyCode === 46 || event.keyCode === 8) {
                // Only handle deletion if the user is not typing in an input field
                if (event.target.tagName !== 'INPUT' && event.target.tagName !== 'TEXTAREA') {
                    setNodes(nds => nds.filter(node => !node.selected));
                    setEdges(eds => eds.filter(edge => !edge.selected));
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [readOnly, setNodes, setEdges]);

    useEffect(() => {
        if (flowData) {
            console.log('BotFlowCanvas received flowData:', flowData);

            // First ensure nodes array exists and is properly structured
            const validNodes = Array.isArray(flowData.nodes)
                ? flowData.nodes.map(node => {
                    const position = typeof node.position === 'object'
                        ? { x: node.position.x || 0, y: node.position.y || 0 }
                        : { x: 0, y: 0 };

                    const data = {
                        ...(node.data || {}),
                        label: node.data?.label || node.type.charAt(0).toUpperCase() + node.type.slice(1)
                    };

                    return {
                        ...node,
                        position,
                        data
                    };
                })
                : [];

            // Set nodes first to ensure they exist before processing edges
            setNodes(validNodes);

            // Create a set of valid node IDs for quick lookup
            const validNodeIds = new Set(validNodes.map(node => node.id));
            console.log("Valid node IDs for edge validation:", Array.from(validNodeIds));

            // Filter and validate edges
            const validEdges = Array.isArray(flowData.edges)
                ? flowData.edges.filter(edge => validNodeIds.has(edge.source) && validNodeIds.has(edge.target))
                    .map(edge => {
                        // Debug logging for edge restoration
                        console.log('Restoring edge:', edge);

                        // Special handling for condition node edges to ensure sourceHandle is preserved
                        const sourceNode = validNodes.find(node => node.id === edge.source);
                        const isConditionSource = sourceNode && sourceNode.type === 'condition';

                        // Determine the correct sourceHandle
                        let sourceHandle = edge.sourceHandle;
                        if (isConditionSource) {
                            // For condition nodes, ensure sourceHandle is the string 'true' or 'false'
                            // based on the condition.result value
                            if (edge.condition && typeof edge.condition.result === 'boolean') {
                                sourceHandle = edge.condition.result ? 'true' : 'false';
                            } else if (edge.label === 'True') {
                                sourceHandle = 'true';
                            } else if (edge.label === 'False') {
                                sourceHandle = 'false';
                            }
                        }

                        return {
                            ...edge,
                            id: edge.id || `e${edge.source}-${edge.target}`,
                            sourceHandle,
                            targetHandle: edge.targetHandle || null,
                            label: edge.label || '',
                            // Apply styling based on edge type
                            style: isConditionSource ? {
                                stroke: sourceHandle === 'true' ? '#10b981' : '#ef4444',
                                strokeWidth: 2
                            } : edge.style || { strokeWidth: 2, stroke: '#6b7280' },
                            markerEnd: {
                                type: 'arrowclosed',
                                color: isConditionSource
                                    ? (sourceHandle === 'true' ? '#10b981' : '#ef4444')
                                    : '#6b7280'
                            }
                        };
                    })
                : [];

            // Log validation results
            if (flowData.edges && flowData.edges.length !== validEdges.length) {
                console.warn(`Removed ${flowData.edges.length - validEdges.length} invalid edges that referenced non-existent nodes`);
            }

            setEdges(validEdges);
        }
    }, [flowData, setNodes, setEdges]);

    const onConnect = useCallback((params) => {
        const sourceNode = nodes.find(node => node.id === params.source);
        const targetNode = nodes.find(node => node.id === params.target);

        if (!sourceNode || !targetNode) return;

        // Create a unique ID for the edge with timestamp to help with sorting
        const edgeId = `e${params.source}-${params.target}-${Date.now()}`;

        let edgeParams = {
            ...params,
            id: edgeId,
            label: '',
            style: { strokeWidth: 2, stroke: '#6b7280' },
            markerEnd: {
                type: 'arrowclosed',
                color: '#6b7280',
            },
        };

        if (sourceNode.type === 'condition') {
            // Make sure sourceHandle is stored as a string
            const sourceHandleValue = params.sourceHandle || 'true';

            // Log what's happening
            console.log('Creating condition edge with sourceHandle:', sourceHandleValue);

            edgeParams.sourceHandle = sourceHandleValue; // Store as string
            edgeParams.label = sourceHandleValue === 'true' ? 'True' : 'False';
            edgeParams.style.stroke = sourceHandleValue === 'true' ? '#10b981' : '#ef4444';
            edgeParams.markerEnd.color = sourceHandleValue === 'true' ? '#10b981' : '#ef4444';
            edgeParams.condition = {
                result: sourceHandleValue === 'true'
            };

            // Remove any existing edge with the same source and sourceHandle to prevent duplicates
            setEdges(eds => {
                // Find existing edges from the same condition output
                const existingEdge = eds.find(e =>
                    e.source === params.source &&
                    e.sourceHandle === sourceHandleValue
                );

                if (existingEdge) {
                    console.log(`Replacing existing ${sourceHandleValue} edge from ${params.source} to ${existingEdge.target}`);
                    return eds.filter(e => e.id !== existingEdge.id);
                }
                return eds;
            });

            // Log the final edge params
            console.log('Final edge params:', edgeParams);
        }

        if (sourceNode.type === 'start') {
            edgeParams.label = 'start';
            edgeParams.style.stroke = '#10b981';
            edgeParams.markerEnd.color = '#10b981';
        } else if (targetNode.type === 'end') {
            edgeParams.label = 'end';
        } else if (targetNode.type === 'handoff') {
            edgeParams.label = 'to_human';
            edgeParams.style.stroke = '#06b6d4';
            edgeParams.markerEnd.color = '#06b6d4';
        } else if (sourceNode.type === 'question' && targetNode.type !== 'condition') {
            edgeParams.label = 'answer';

            // For question nodes, remove any existing 'answer' edges to prevent multiple answer paths
            setEdges(eds => {
                const existingAnswerEdge = eds.find(e =>
                    e.source === params.source &&
                    e.label === 'answer'
                );

                if (existingAnswerEdge) {
                    console.log(`Replacing existing answer edge from ${params.source} to ${existingAnswerEdge.target}`);
                    return eds.filter(e => e.id !== existingAnswerEdge.id);
                }
                return eds;
            });
        } else if (edgeParams.label === '') {
            edgeParams.label = 'next';
        }

        setEdges((eds) => addEdge(edgeParams, eds));
    }, [nodes, setEdges]);

    const onNodeClick = useCallback((_, node) => {
        if (readOnly) return;
        setSelectedNode(node);
        setShowPropertyPanel(true);
    }, [readOnly]);

    const onUpdateNode = useCallback((id, data) => {
        setNodes((nds) =>
            nds.map((node) => {
                if (node.id === id) {
                    return { ...node, data: { ...data } };
                }
                return node;
            })
        );
    }, [setNodes]);

    const onAddNode = useCallback((type) => {
        if (!reactFlowInstance) return;

        const position = reactFlowInstance.project({
            x: Math.random() * 300 + 100,
            y: Math.random() * 300 + 100,
        });

        const newNode = {
            id: `${type}_${Date.now()}`,
            type,
            position,
            data: { label: type.charAt(0).toUpperCase() + type.slice(1) },
        };

        setNodes((nds) => nds.concat(newNode));
    }, [reactFlowInstance, setNodes]);

    const handleSave = () => {
        if (onSave) {
            const flowToSave = {
                ...(flowData || {}),
                name: flowData?.name || 'Untitled Flow',
                description: flowData?.description || '',
                nodes: nodes.map(node => ({
                    id: node.id,
                    type: node.type,
                    position: {
                        x: node.position.x,
                        y: node.position.y
                    },
                    data: {
                        ...node.data,
                        label: node.data.label || node.type.charAt(0).toUpperCase() + node.type.slice(1),
                        selected: undefined,
                        dragging: undefined,
                        ...(node.type === 'message' && !node.data.buttons && { buttons: [] }),
                        ...(node.type === 'question' && !node.data.quickReplies && { quickReplies: [] }),
                        ...(node.type === 'condition' && !node.data.condition && {
                            condition: { field: '', operator: 'equals', value: '' }
                        }),
                        ...(node.type === 'action' && !node.data.action && {
                            action: { type: 'setVariable', config: {} }
                        })
                    }
                })),
                edges: edges.map(edge => ({
                    id: edge.id,
                    source: edge.source,
                    sourceHandle: edge.sourceHandle || null,
                    target: edge.target,
                    targetHandle: edge.targetHandle || null,
                    label: edge.label || '',
                    ...(edge.condition && { condition: edge.condition })
                })),
                variables: flowData?.variables || [],
                settings: flowData?.settings || {
                    timeout: 300000,
                    maxRetries: 3,
                    fallbackMessage: "I didn't understand that. Please try again."
                },
                isActive: flowData?.isActive || false,
                isDefault: flowData?.isDefault || false,
                version: (flowData?.version || 0) + 1
            };

            // Pass an additional options object with the fullscreen state to maintain it
            onSave(flowToSave, {
                maintainFullscreen: isFullscreen
            });
        }
    };

    const toggleFullscreen = () => {
        setIsFullscreen(!isFullscreen);
    };

    const handleTest = () => {
        if (onTest) {
            onTest({
                nodes,
                edges,
                variables: flowData?.variables || [],
                settings: flowData?.settings || {},
            });
        }
    };

    const handleDeleteSelected = () => {
        setNodes(nds => nds.filter(node => !node.selected));
        setEdges(eds => eds.filter(edge => !edge.selected));
    };

    return (
        <div className={`${isFullscreen ? 'fixed inset-0 z-50' : 'h-full'} w-full flex flex-col bg-gray-50`}>
            {/* Enhanced Header */}
            {!readOnly && (
                <div className="p-4 border-b border-gray-200 bg-white shadow-sm">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-4">
                            <div>
                                <h1 className="text-xl font-bold text-gray-900">{flowData?.name || 'New Flow'}</h1>
                                {flowData?.description && (
                                    <p className="text-sm text-gray-600">{flowData.description}</p>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                                    {nodes.length} nodes
                                </span>
                                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                                    {edges.length} connections
                                </span>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {/* Canvas Controls */}
                            <div className="flex items-center gap-1 mr-4">
                                <button
                                    onClick={() => setShowNodePalette(!showNodePalette)}
                                    className={`p-2 rounded-lg transition-colors ${showNodePalette ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                    title="Toggle Node Palette"
                                >
                                    <PlusIcon className="h-4 w-4" />
                                </button>
                                <button
                                    onClick={() => setShowMiniMap(!showMiniMap)}
                                    className={`p-2 rounded-lg transition-colors ${showMiniMap ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                    title="Toggle MiniMap"
                                >
                                    {showMiniMap ? <EyeIcon className="h-4 w-4" /> : <EyeSlashIcon className="h-4 w-4" />}
                                </button>
                                <button
                                    onClick={toggleFullscreen}
                                    className="p-2 bg-gray-100 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                                    title="Toggle Fullscreen"
                                >
                                    {isFullscreen ?
                                        <ArrowsPointingInIcon className="h-4 w-4" /> :
                                        <ArrowsPointingOutIcon className="h-4 w-4" />
                                    }
                                </button>
                            </div>

                            {/* Action Buttons */}
                            <button
                                onClick={handleTest}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium shadow-sm flex items-center gap-2"
                            >
                                <PlayIcon className="h-4 w-4" />
                                Test Flow
                            </button>
                            <button
                                onClick={handleSave}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm flex items-center gap-2"
                            >
                                <BookmarkIcon className="h-4 w-4" />
                                Save Flow
                            </button>
                            {isFullscreen && (
                                <button
                                    onClick={toggleFullscreen}
                                    className="p-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                                    title="Exit Fullscreen"
                                >
                                    <XMarkIcon className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Canvas Area */}
            <div className="flex-1 relative" ref={reactFlowWrapper}>
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={readOnly ? undefined : handleNodesChange}
                    onEdgesChange={readOnly ? undefined : handleEdgesChange}
                    onConnect={readOnly ? undefined : onConnect}
                    onInit={onInit}
                    onNodeClick={onNodeClick}
                    nodeTypes={nodeTypes}
                    fitView
                    snapToGrid
                    snapGrid={[10, 10]}
                    defaultEdgeOptions={{
                        style: { strokeWidth: 2, stroke: '#6b7280' },
                        markerEnd: { type: 'arrowclosed', color: '#6b7280' },
                    }}
                    nodesDraggable={!readOnly}
                    nodesConnectable={!readOnly}
                    elementsSelectable={!readOnly}
                >
                    <Controls
                        showZoom={true}
                        showFitView={true}
                        showInteractive={!readOnly}
                        className="bg-white shadow-lg rounded-lg border border-gray-200"
                    />
                    {!readOnly && (
                        <Panel position="bottom-right" className="bg-white rounded-lg shadow-lg border border-gray-200 p-1 mb-20 mr-4">
                            <button
                                onClick={handleDeleteSelected}
                                className="p-2 text-red-500 hover:bg-red-50 rounded transition-colors flex items-center gap-2"
                                title="Delete selected (Delete/Backspace key)"
                            >
                                <TrashIcon className="h-5 w-5" />
                                <span className="text-sm font-medium">Delete</span>
                            </button>
                        </Panel>
                    )}
                    {showMiniMap && (
                        <MiniMap
                            nodeStrokeColor={(n) => '#374151'}
                            nodeColor={(n) => {
                                switch (n.type) {
                                    case 'start': return '#10b981';
                                    case 'message': return '#3b82f6';
                                    case 'question': return '#8b5cf6';
                                    case 'condition': return '#f59e0b';
                                    case 'action': return '#ef4444';
                                    case 'handoff': return '#06b6d4';
                                    case 'end': return '#6b7280';
                                    default: return '#9ca3af';
                                }
                            }}
                            className="bg-white border border-gray-200 rounded-lg shadow-lg"
                        />
                    )}
                    <Background
                        variant="dots"
                        gap={20}
                        size={1}
                        color="#e5e7eb"
                    />

                    {/* Node Palette */}
                    {showNodePalette && !readOnly && (
                        <NodePalette
                            onAddNode={onAddNode}
                            isCollapsed={nodePaletteCollapsed}
                            onToggle={() => setNodePaletteCollapsed(!nodePaletteCollapsed)}
                        />
                    )}
                </ReactFlow>

                {/* Property Panel */}
                <PropertyPanel
                    selectedNode={selectedNode}
                    onUpdateNode={onUpdateNode}
                    onClose={() => setShowPropertyPanel(false)}
                    isOpen={showPropertyPanel}
                />

                {/* Overlay for property panel */}
                {showPropertyPanel && (
                    <div
                        className="fixed inset-0 bg-black bg-opacity-20 z-20"
                        onClick={() => setShowPropertyPanel(false)}
                    />
                )}

                {/* Floating Delete Button - Only visible when nodes are selected */}
                {!readOnly && nodes.some(node => node.selected) && (
                    <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-20">
                        <button
                            onClick={handleDeleteSelected}
                            className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg shadow-lg hover:bg-red-600 transition-colors"
                        >
                            <TrashIcon className="h-5 w-5" />
                            <span>Delete Selected</span>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

const BotFlowCanvasWrapper = (props) => (
    <ReactFlowProvider>
        <BotFlowCanvas {...props} />
    </ReactFlowProvider>
);

export default BotFlowCanvasWrapper;